/**
 * App-wide singleton services shared across views.
 *
 * - {@link ws}: the one WebSocket connection to the gateway. Views and the
 *   store subscribe to it; it is connected once during bootstrap.
 * - {@link mapHost}: a single {@link FleetMap} instance wrapped in a detached
 *   DOM node that can be re-parented into whichever view currently needs the
 *   map (Overview, Live Map). This avoids re-initializing the heavy deck.gl /
 *   Leaflet stack every time the user switches views.
 * @module app/services
 */

import { apiFetch } from "../auth.ts";
import { store } from "../state/store.ts";
import { WSClient } from "../ws/client.ts";

/** Single gateway WebSocket connection for the whole app. */
export const ws = new WSClient(
	`${location.protocol === "https:" ? "wss:" : "ws:"}//${location.host}/ws`,
);

type FleetMapType = import("../map/fleet-map.ts").FleetMap;

/**
 * Owns the one-and-only {@link FleetMap}. The map lives inside a persistent
 * wrapper element that is moved between view containers on demand.
 */
class MapHost {
	private wrapper: HTMLElement | null = null;
	private map: FleetMapType | null = null;
	private rafId: number | null = null;
	private vehicleClick: ((id: string) => void) | null = null;
	private geofencesLoaded = false;

	/** The persistent wrapper element (created on first access). */
	private getWrapper(): HTMLElement {
		if (!this.wrapper) {
			this.wrapper = document.createElement("div");
			this.wrapper.className = "map-host";
			const inner = document.createElement("div");
			inner.id = "fleet-map";
			inner.className = "map-host-canvas";
			this.wrapper.appendChild(inner);
		}
		return this.wrapper;
	}

	/** Lazily construct the FleetMap once its element is in the DOM. */
	private async ensureMap(): Promise<FleetMapType> {
		if (this.map) return this.map;
		const { FleetMap } = await import("../map/fleet-map.ts");
		this.map = new FleetMap("fleet-map");
		this.map.setVehicleClickHandler((id) => this.vehicleClick?.(id));
		this.map.updateVehicles(store.getVehicles());
		await this.loadGeofences();
		return this.map;
	}

	/** Set the handler invoked when a vehicle dot is clicked on the map. */
	setVehicleClickHandler(fn: ((id: string) => void) | null): void {
		this.vehicleClick = fn;
	}

	/**
	 * Move the map into `parent`, constructing it on first use. Returns the live
	 * FleetMap so callers can drive toolbar actions (fit, toggles, focus).
	 */
	async attachTo(parent: HTMLElement): Promise<FleetMapType> {
		parent.appendChild(this.getWrapper());
		const map = await this.ensureMap();
		// Leaflet needs an explicit size recompute after re-parenting.
		requestAnimationFrame(() => map.resize());
		return map;
	}

	/** Detach the map wrapper from the DOM (kept alive for the next view). */
	detach(): void {
		this.wrapper?.remove();
		this.vehicleClick = null;
	}

	/** Recompute the map size (e.g. on window resize). No-op if not mounted. */
	resize(): void {
		this.map?.resize();
	}

	/** Push the latest store vehicle snapshots to the map (coalesced per frame). */
	scheduleUpdate(): void {
		if (!this.map || this.rafId !== null) return;
		this.rafId = requestAnimationFrame(() => {
			this.rafId = null;
			this.map?.updateVehicles(store.getVehicles());
		});
	}

	/** Load geofence polygons onto the map once. */
	async loadGeofences(): Promise<void> {
		if (this.geofencesLoaded || !this.map) return;
		try {
			const res = await apiFetch("/api/v1/geofences");
			if (res.ok) {
				const geofences = await res.json();
				this.map.drawGeofences(Array.isArray(geofences) ? geofences : []);
				this.geofencesLoaded = true;
			}
		} catch (err) {
			console.error("Failed to load geofences:", err);
		}
	}
}

export const mapHost = new MapHost();

// Keep the map in sync with live telemetry regardless of which view is active.
store.subscribe(() => mapHost.scheduleUpdate());
